import { useMemo } from 'react';
import {
  anchorHit,
  cardMultiplier,
  cardScore,
  isEntryEditable,
  type Entry,
  type Fixture,
  type Prediction,
  type Round
} from '@h2h/shared';
import { relativeDay } from '../play/helpers';
import { buildCardRows, buildLiveResult } from './buildCardRows';
import { teamCode } from './helpers';
import type { CardRow } from './types';

export interface NoPickAction {
  label: string;
  onClick: () => void;
  primary: boolean;
}

/** The glanceable "your card right now" read for the live screen. */
export interface LiveCardVM {
  /** Points this card banks if the match ended at the current scoreline. */
  points: number;
  /** Is the 1X2 base currently landing (card banking) or busting? */
  baseHit: boolean;
  /** Combined boost multiplier on the card. */
  multiplier: number;
  /** Boosts currently on track / total boosts stacked. */
  boostsHitting: number;
  boostsTotal: number;
}

export interface FootballMatchDetailVM {
  home: string;
  away: string;
  nationalTeams: boolean;
  result: Fixture['result'];
  final: boolean;
  live: boolean;
  showHome: number;
  showAway: number;
  liveResult: ReturnType<typeof buildLiveResult>;
  events: NonNullable<Fixture['events']>;
  rows: CardRow[];
  myPoints: number | null;
  /** Present only while live with a card on this match — drives the live banner. */
  liveCard: LiveCardVM | null;
  showLiveLink: boolean;
  anyRoundLive: boolean;
  noPickMessage: string;
  noPickAction: NoPickAction | null;
}

export function useFootballMatchDetailVM({
  fixture,
  fixtures,
  entry,
  round,
  myPick,
  nationalTeams,
  goPicks,
  goCompete
}: {
  fixture: Fixture;
  fixtures: Fixture[];
  entry: Entry | undefined;
  round: Round | undefined;
  myPick: Prediction | undefined;
  nationalTeams: boolean;
  goPicks: () => void;
  goCompete: () => void;
}): FootballMatchDetailVM {
  return useMemo(() => {
    const home = teamCode(fixture.homeTeam, fixture.homeCode);
    const away = teamCode(fixture.awayTeam, fixture.awayCode);
    const result = fixture.result;
    const final = fixture.status === 'SETTLED' && !!result;
    const live = fixture.status === 'LIVE';

    const liveH = fixture.liveHomeGoals ?? 0;
    const liveA = fixture.liveAwayGoals ?? 0;
    const showHome = final ? result!.homeGoals : liveH;
    const showAway = final ? result!.awayGoals : liveA;

    const liveResult = buildLiveResult(fixture, live);
    const events = fixture.events ?? [];

    const rows: CardRow[] = myPick ? buildCardRows(myPick, fixture, final, result, live, liveResult) : [];

    const myPoints = myPick && final && result ? (myPick.cardScore ?? cardScore(myPick, result)) : null;

    // Live "if it ends now" read — the headline of the watch-along screen.
    const liveCard: LiveCardVM | null =
      myPick && live && liveResult
        ? {
            points: cardScore(myPick, liveResult),
            baseHit: anchorHit(myPick, liveResult),
            multiplier: cardMultiplier(myPick.legs ?? []),
            // rows[0] is the 1X2 base; the rest are the stacked boosts.
            boostsHitting: rows.slice(1).filter((r) => r.state === 'ontrack').length,
            boostsTotal: rows.length - 1
          }
        : null;

    const now = Date.now();
    const roundOpen = round?.status === 'OPEN' || round?.status === 'LOCKING';
    const minOpenToEnter = round?.pickMode === 'FLEXIBLE' ? 1 : (round?.picksRequired ?? 0);
    const openCount = fixtures.filter((f) => f.status === 'SCHEDULED' && f.kickoffAt > now).length;
    const canEnterNew =
      !!round && isEntryEditable(round, [], now) && minOpenToEnter > 0 && openCount >= minOpenToEnter;

    const anyRoundLive = fixtures.some((f) => f.status === 'LIVE');
    const showLiveLink =
      !!entry && (live || final || round?.status === 'LOCKING' || round?.status === 'SETTLING');

    const myPickKickoffs = (entry?.predictions ?? [])
      .map((p) => fixtures.find((x) => x.fixtureId === p.fixtureId)?.kickoffAt)
      .filter((k): k is number => k != null);
    const myCardEditable = !!entry && !!round && isEntryEditable(round, myPickKickoffs, now);

    let noPickMessage: string;
    let noPickAction: NoPickAction | null = null;
    if (entry) {
      if (myCardEditable && !myPick) {
        noPickMessage = 'No card on this match yet.';
        noPickAction = { label: 'Build this card', onClick: goPicks, primary: true };
      } else if (myCardEditable) {
        noPickMessage = 'You have cards on other matches this round.';
        noPickAction = { label: 'Edit your cards', onClick: goPicks, primary: true };
      } else {
        noPickMessage = 'You didn’t build a card on this match.';
      }
    } else if (canEnterNew) {
      noPickMessage = 'No card on this match yet.';
      noPickAction = { label: 'Build this card', onClick: goPicks, primary: true };
    } else if (roundOpen) {
      noPickMessage =
        round?.pickMode === 'FLEXIBLE'
          ? 'Cards closed — too few matches left to enter.'
          : 'Cards closed — this round locked at first kickoff.';
      noPickAction = { label: 'Back to Compete', onClick: goCompete, primary: false };
    } else if (round?.status === 'SCHEDULED') {
      noPickMessage = `Cards open ${relativeDay(round.openAt)}.`;
      noPickAction = { label: 'Back to Compete', onClick: goCompete, primary: false };
    } else {
      noPickMessage = 'You didn’t build a card on this match.';
    }

    return {
      home,
      away,
      nationalTeams,
      result,
      final,
      live,
      showHome,
      showAway,
      liveResult,
      events,
      rows,
      myPoints,
      liveCard,
      showLiveLink,
      anyRoundLive,
      noPickMessage,
      noPickAction
    };
  }, [entry, fixture, fixtures, goCompete, goPicks, myPick, nationalTeams, round]);
}
